import React, { useRef, useEffect, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import practiceLocationsData from '../../data/practiceLocations.json';

// Component to handle label positioning based on map coordinates
const CountyLabels = ({ proportionalCircles, hoveredCircle }) => {
  const map = useMap();
  const [labelPositions, setLabelPositions] = useState([]);

  useEffect(() => {
    const updatePositions = () => {
      const positions = proportionalCircles.map(circle => {
        const point = map.latLngToContainerPoint([circle.center.lat, circle.center.lng]);
        return {
          ...circle,
          x: point.x,
          y: point.y
        };
      });
      setLabelPositions(positions);
    };

    // Update positions when map moves, zooms, or loads
    updatePositions();
    map.on('moveend zoomend', updatePositions);

    return () => {
      map.off('moveend zoomend', updatePositions);
    };
  }, [map, proportionalCircles]);

  // Only show label for hovered circle
  const visibleLabels = labelPositions.filter(circle => 
    hoveredCircle && circle.county === hoveredCircle
  );

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1000 }}>
      {visibleLabels.map((circle, i) => (
        <div
          key={`label-${i}`}
          className="absolute bg-white bg-opacity-95 px-2 py-1 rounded-md text-xs font-bold text-blue-800 border-2 border-blue-400 shadow-lg transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{
            left: `${circle.x}px`,
            top: `${circle.y}px`,
            fontSize: '11px',
            lineHeight: '1.3',
            zIndex: 1001
          }}
        >
          <div className="text-center whitespace-nowrap">
            <div className="font-bold">{circle.county}</div>
            <div>{circle.count} Registrants</div>
            <div>{circle.percentage}%</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Custom component for circle with label that handles hover
const CircleWithLabel = ({ center, radius, county, count, percentage, fillColor, color, weight, opacity, fillOpacity, onHover, onLeave }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseOver = () => {
    setIsHovered(true);
    onHover(county);
  };

  const handleMouseOut = () => {
    setIsHovered(false);
    onLeave();
  };

  return (
    <>
      <Circle
        center={center}
        radius={radius}
        pathOptions={{
          fillColor,
          color,
          weight,
          opacity,
          fillOpacity: isHovered ? fillOpacity + 0.1 : fillOpacity // Slightly more opaque on hover
        }}
        eventHandlers={{
          mouseover: handleMouseOver,
          mouseout: handleMouseOut
        }}
      >
        <Popup>
          <div className="text-center">
            <h3 className="font-bold text-lg">{county} County</h3>
            <p className="text-lg font-semibold text-blue-600">{count} Registrants</p>
            <p className="text-xs text-gray-500">{percentage}% of total</p>
          </div>
        </Popup>
      </Circle>
    </>
  );
};

const FloridaCMEMap = () => {
  const [hoveredCircle, setHoveredCircle] = useState(null);

  const handleCircleHover = (county) => {
    setHoveredCircle(county);
  };

  const handleCircleLeave = () => {
    setHoveredCircle(null);
  };

  const zipLatLng = {
    '33323': { lat: 26.1290, lng: -80.3140 },
    '34109': { lat: 26.2540, lng: -81.7730 },
    '34104': { lat: 26.1529, lng: -81.7317 },
    '34110': { lat: 26.2960, lng: -81.7977 },
    '34142': { lat: 26.4187, lng: -81.4199 },
    '34108': { lat: 26.2404, lng: -81.8080 },
    '33578': { lat: 27.8661, lng: -82.3263 },
    '33907': { lat: 26.5569, lng: -81.8705 },
    '33908': { lat: 26.5029, lng: -81.9500 },
    '33912': { lat: 26.4899, lng: -81.8285 },
    '34135': { lat: 26.3483, lng: -81.7787 },
    '33901': { lat: 26.6251, lng: -81.8820 },
    '32301': { lat: 30.4313, lng: -84.2696 },
    '01720': { lat: 42.4851, lng: -71.4328 },
    '00820': { lat: 17.7470, lng: -64.7046 },
    '00840': { lat: 17.7120, lng: -64.8809 },
    '01606': { lat: 42.3116, lng: -71.7955 }
  };

  const classify = (zip) => {
    if (/^0082|00840/.test(zip)) return { label: 'USVI Practice' };
    if (/^016|017/.test(zip))    return { label: 'Out-of-State (MA)' };
    if (/^33|34/.test(zip))      return { label: 'Florida Practice' };
    return { label: 'Other' };
  };

  const PRACTICE_COLOR = '#10B981'; // Green color for all practices

  // County center coordinates for proportional circles
  const countyCenters = {
    'Collier': { lat: 26.3384, lng: -81.3789 },
    'Lee': { lat: 26.5628, lng: -81.7787 }
  };

  // Process all individual registrants and create individual dots
  const allRegistrants = React.useMemo(() => {
    const registrants = [];
    
    // Group registrants by ZIP code to create better offsets
    const registrantsByZip = {};
    practiceLocationsData.forEach((registrant, index) => {
      const zip = (registrant.zip || '').toString().trim();
      if (!registrantsByZip[zip]) {
        registrantsByZip[zip] = [];
      }
      registrantsByZip[zip].push({ ...registrant, originalIndex: index });
    });
    
    // Create dots with random distribution for each ZIP group
    Object.entries(registrantsByZip).forEach(([zip, zipRegistrants]) => {
      const geo = zipLatLng[zip];
      if (!geo) return;
      
      const { label } = classify(zip);
      
      zipRegistrants.forEach((registrant, indexInZip) => {
        let offsetLat = geo.lat;
        let offsetLng = geo.lng;
        
        // Create random distribution for multiple registrants in same ZIP
        if (zipRegistrants.length > 1) {
          // Use a deterministic random based on registrant name for consistency
          const seed = registrant.firstName.charCodeAt(0) + registrant.lastName.charCodeAt(0) + indexInZip;
          const random1 = ((seed * 9301 + 49297) % 233280) / 233280;
          const random2 = ((seed * 1663 + 93247) % 233280) / 233280;
          
          // Create a radius based on number of registrants
          const maxRadius = 0.008 + (zipRegistrants.length * 0.0005); // Spread increases with count
          const radius = random1 * maxRadius;
          const angle = random2 * 2 * Math.PI;
          
          offsetLat = geo.lat + (Math.cos(angle) * radius);
          offsetLng = geo.lng + (Math.sin(angle) * radius);
        }
        
        registrants.push({
          id: `${registrant.firstName}-${registrant.lastName}-${registrant.originalIndex}`,
          name: registrant.practice,
          provider: `${registrant.firstName} ${registrant.lastName}`.trim(),
          zipCode: zip,
          county: registrant.county,
          lat: offsetLat,
          lng: offsetLng,
          type: label,
          color: PRACTICE_COLOR
        });
      });
    });
    
    return registrants;
  }, []);

  // Count registrants by county for proportional circles
  const countyStats = React.useMemo(() => {
    const stats = {};
    practiceLocationsData.forEach(registrant => {
      const county = registrant.county;
      if (!stats[county]) {
        stats[county] = 0;
      }
      stats[county]++;
    });
    return stats;
  }, []);

  // Create proportional circles for counties with >5 registrants
  const proportionalCircles = React.useMemo(() => {
    const circles = [];
    const totalRegistrants = 46;
    
    // Define countyData here instead of referencing it from outside
    const countyData = [
      { county: 'Collier', count: 25, center: countyCenters['Collier'] },
      { county: 'Lee', count: 16, center: countyCenters['Lee'] }
    ];
    
    countyData.forEach(({ county, count, center }) => {
      if (count > 5 && center) {
        const percentage = ((count / totalRegistrants) * 100).toFixed(1);
        const radius = Math.sqrt(count) * 1500; // Proportional radius in meters
        circles.push({
          county,
          count,
          percentage,
          center,
          radius
        });
      }
    });
    
    return circles;
  }, []);

  const floridaCenter = [27.8, -82.5];
  const floridaZoom = 7;

  return (
    <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200 relative">
      <MapContainer 
        center={floridaCenter} 
        zoom={floridaZoom} 
        scrollWheelZoom={true}
        dragging={true}
        touchZoom={true}
        doubleClickZoom={true}
        zoomControl={true}
        style={{ height: '100%', width: '100%' }}
        className="leaflet-container"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Proportional circles for counties with >5 registrants */}
        {proportionalCircles.map((circle, i) => (
          <CircleWithLabel
            key={`county-circle-${i}`}
            center={[circle.center.lat, circle.center.lng]}
            radius={circle.radius}
            county={circle.county}
            count={circle.count}
            percentage={circle.percentage}
            fillColor="#3B82F6"
            color="#1E40AF"
            weight={2}
            opacity={0.6}
            fillOpacity={0.1}
            onHover={handleCircleHover}
            onLeave={handleCircleLeave}
          />
        ))}

        {/* Individual registrant dots - all 46 dots */}
        {allRegistrants.map((registrant, i) => (
          <CircleMarker 
            key={`registrant-${i}`} 
            center={[registrant.lat, registrant.lng]} 
            radius={5} 
            fillColor={registrant.color} 
            color="#FFFFFF" 
            weight={1} 
            opacity={1} 
            fillOpacity={0.9}
          >
            <Popup>
              <div className="text-center min-w-48">
                <h4 className="font-bold text-sm text-gray-900 mb-1">{registrant.name}</h4>
                <p className="text-xs text-gray-600 mb-1">{registrant.provider}</p>
                <p className="text-xs text-gray-600 mb-1">County: {registrant.county}</p>
                <p className="text-xs text-gray-600 mb-1">ZIP: {registrant.zipCode}</p>
                <p className="text-xs font-medium" style={{ color: registrant.color }}>{registrant.type}</p>
              </div>
            </Popup>
          </CircleMarker>
        ))}

        {/* County labels positioned dynamically inside circles - only show on hover */}
        <CountyLabels proportionalCircles={proportionalCircles} hoveredCircle={hoveredCircle} />
      </MapContainer>
    </div>
  );
};

export default FloridaCMEMap;
